'use client';
import React, { useEffect, useRef, useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What is the minimum order quantity?',
    answer:
      'Minimum orders start at 12 units for most products, including Fleet Caps and Executive Utility items. The Hospitality Suite coaster sets start at 24 units. Volume pricing kicks in at 50+ units, with significant savings at 100+ and 500+ unit tiers.',
    category: 'Pricing',
  },
  {
    question: 'How long does production and delivery take?',
    answer:
      'Standard production runs 10–14 business days from artwork approval. Rush orders (7 business days) are available for an additional fee. Shipping is calculated at checkout based on destination and order volume. We ship across North America.',
    category: 'Onboarding',
  },
  {
    question: 'What makes your 3D patches different from standard embroidery?',
    answer:
      'Our 3D tactile leather patches use a proprietary flexible polymer base that allows the patch to flex with the fabric without cracking — even after years of daily wear. Standard embroidery is flat. Standard PVC patches crack. Our process delivers a premium leather-feel texture with full-color UV-resistant printing that competitors simply cannot replicate.',
    category: 'Product',
  },
  {
    question: 'Can I get a sample before placing a full order?',
    answer:
      'Yes. We offer pre-production samples for orders over 50 units. Sample cost is credited toward your full order. For orders under 50 units, we provide a digital proof with a 3D render before production begins. We don’t start production until you’ve approved the artwork.',
    category: 'Onboarding',
  },
  {
    question: 'Do you offer fixed-price quotes or is everything custom?',
    answer:
      'Both. Our standard product lines (Fleet Caps, Coaster Sets, Keychain Sets, Pen Sets) have transparent per-unit pricing based on quantity. Custom shapes, sizes, or materials are scoped and priced upfront — you always know the number before work begins. No hourly billing surprises.',
    category: 'Pricing',
  },
  {
    question: 'What file formats do you accept for artwork?',
    answer:
      'We accept vector files (AI, EPS, SVG) for best results. High-resolution PNG or PDF files (300 DPI minimum) are also accepted. If you only have a low-resolution logo, our design team can redraw it in vector format for a flat fee. We’ll never print a blurry logo.',
    category: 'Product',
  },
  {
    question: 'Can the QR codes on hospitality products be updated after production?',
    answer:
      'Yes. All QR codes are dynamic — they point to a redirect URL that you control through our client portal. You can update the destination URL at any time without reprinting a single coaster or keychain. Your QR code always points to the latest version of your business.',
    category: 'Product',
  },
  {
    question: 'Do you work with businesses outside the US?',
    answer:
      'Absolutely. While we have deep roots in North American markets, we ship to Canada, the UK, Australia, and select international destinations. Contact us for international shipping quotes. All pricing is in USD.',
    category: 'Pricing',
  },
];

const categories = ['All Topics', 'Pricing', 'Onboarding', 'Product'];

export default function FAQSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeCategory, setActiveCategory] = React.useState('All Topics');
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedEls = sectionRef.current?.querySelectorAll('.animate-faq');
    animatedEls?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const filteredFaqs =
    activeCategory === 'All Topics'
      ? faqs
      : faqs.filter((faq) => faq.category === activeCategory);

  return (
    <section
      id="faq"
      ref={sectionRef}
      className="py-20 px-4 relative overflow-hidden"
      style={{ backgroundColor: 'rgb(3, 8, 16)' }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="section-label">Got Questions?</p>
          <h2
            className="animate-faq opacity-0 translate-y-8 transition-all duration-700 font-montserrat font-black text-white mb-4"
            style={{ fontSize: 'clamp(28px, 4vw, 48px)', letterSpacing: '-1px' }}
          >
            Common Questions,{' '}
            <span style={{ color: '#00b4ff' }}>Straight Answers</span>
          </h2>
          <p
            className="animate-faq opacity-0 translate-y-8 transition-all duration-700"
            style={{ color: 'rgb(136, 147, 152)', fontSize: '18px', transitionDelay: '100ms' }}
          >
            Everything you need to know about ordering, production, and what makes Serious Swag
            different — no sales fluff.
          </p>
        </div>

        {/* Category Filter */}
        <div
          className="animate-faq opacity-0 translate-y-8 transition-all duration-700 flex flex-wrap gap-2 justify-center mb-8"
          style={{ transitionDelay: '150ms' }}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="font-montserrat font-bold uppercase text-sm rounded px-4 py-2 transition-all duration-200"
              style={{
                letterSpacing: '0.5px',
                backgroundColor:
                  activeCategory === cat ? '#00b4ff' : 'rgba(255,255,255,0.06)',
                color: activeCategory === cat ? '#0a192f' : 'rgba(255,255,255,0.7)',
                border:
                  activeCategory === cat
                    ? '1px solid #00b4ff' :'1px solid rgba(255,255,255,0.1)',
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div
          className="animate-faq opacity-0 translate-y-8 transition-all duration-700 rounded-lg border overflow-hidden"
          style={{ borderColor: '#1a3a5c', transitionDelay: '200ms' }}
        >
          {filteredFaqs.map((faq, index) => (
            <div
              key={faq.question}
              className="border-b last:border-b-0"
              style={{ borderColor: '#1a3a5c' }}
            >
              <button
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left group"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span
                  className="font-montserrat font-bold text-white text-base group-hover:text-blue-400 transition-colors duration-200"
                >
                  {faq.question}
                </span>
                <span
                  className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200"
                  style={{
                    backgroundColor:
                      openIndex === index ? '#00b4ff' : 'rgba(255,255,255,0.08)',
                    transform: openIndex === index ? 'rotate(45deg)' : 'rotate(0deg)',
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M6 2v8M2 6h8"
                      stroke={openIndex === index ? '#0a192f' : 'white'}
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </button>
              {openIndex === index && (
                <div
                  className="px-6 pb-5"
                  style={{ color: 'rgb(136, 147, 152)', fontSize: '15px', lineHeight: '1.7' }}
                >
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Still have questions */}
        <div
          className="animate-faq opacity-0 translate-y-8 transition-all duration-700 text-center mt-10"
          style={{ transitionDelay: '300ms' }}
        >
          <p
            className="mb-4"
            style={{ color: 'rgb(136, 147, 152)' }}
          >
            Still have a question we didn&apos;t cover?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 font-montserrat font-bold uppercase text-white rounded px-6 py-3 transition-all duration-300"
            style={{
              border: '2px solid rgba(0, 180, 255, 0.5)',
              letterSpacing: '0.5px',
              fontSize: '14px',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = '#00b4ff';
              (e.currentTarget as HTMLElement).style.color = '#00b4ff';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0, 180, 255, 0.5)';
              (e.currentTarget as HTMLElement).style.color = '#ffffff';
            }}
          >
            Ask Us a Question
          </a>
        </div>
      </div>
    </section>
  );
}
